// Loading / empty / error / not-implemented / unauthorized states, plus an
// ApiState boundary that maps an async result to the right honest state.
// Crucially: a 404/501 renders "being finalized" — NEVER fabricated data.
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import type { AsyncStatus } from '../lib/hooks';
import { IconInbox, IconWarning, IconError, IconInfo } from './Icons';
import { Button, Spinner } from './ui';

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="state" role="status" aria-live="polite">
      <Spinner large />
      <p className="state__body">{label ?? t('common.loading')}</p>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  icon,
  action,
}: {
  title: string;
  body?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <div className="state__icon">{icon ?? <IconInbox width={26} height={26} />}</div>
      <p className="state__title">{title}</p>
      {body ? <p className="state__body">{body}</p> : null}
      {action}
    </div>
  );
}

export function PendingState() {
  const { t } = useTranslation();
  return (
    <div className="state" role="status">
      <div className="state__icon state__icon--warn">
        <IconInfo width={26} height={26} />
      </div>
      <p className="state__title">{t('common.unavailableTitle')}</p>
      <p className="state__body">{t('common.unavailableBody')}</p>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="state" role="alert">
      <div className="state__icon state__icon--error">
        <IconError width={26} height={26} />
      </div>
      <p className="state__title">{t('common.errorTitle')}</p>
      <p className="state__body">{error.message}</p>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  );
}

export function UnauthorizedState() {
  const { t } = useTranslation();
  return (
    <div className="state">
      <div className="state__icon state__icon--warn">
        <IconWarning width={26} height={26} />
      </div>
      <p className="state__title">{t('common.unauthorizedTitle')}</p>
      <p className="state__body">{t('common.unauthorizedBody')}</p>
      <Link className="btn btn--primary" to="/">
        {t('common.login')}
      </Link>
    </div>
  );
}

/**
 * Renders the correct state for an async result and only calls `children`
 * once data is present. 404/501 => PendingState (honest "being finalized").
 */
export function ApiState<T>({
  status,
  error,
  data,
  onRetry,
  children,
}: {
  status: AsyncStatus;
  error: ApiError | null;
  data: T | null;
  onRetry?: () => void;
  children: (data: T) => ReactNode;
}) {
  if (status === 'loading') return <LoadingState />;
  if (status === 'error' && error) {
    if (error.isNotImplemented) return <PendingState />;
    if (error.isUnauthorized) return <UnauthorizedState />;
    return <ErrorState error={error} onRetry={onRetry} />;
  }
  if (data == null) return <LoadingState />;
  return <>{children(data)}</>;
}
